import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Section } from "@/components/app/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import TID from "@/lib/testIds";

export default function AdminConsole() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [sel, setSel] = useState(null);
  const [uploading, setUploading] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    const r = await api.get("/tenants");
    const list = r.data.filter((t) => !t.is_propay && !t.is_network);
    setTenants(list);
    // Functional setter — no need to depend on `sel`.
    setSel((cur) => cur ?? list.find((t) => t.id === user.tenant_id) ?? list[0] ?? null);
  }, [user.tenant_id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!sel) return <div>Loading…</div>;

  const save = async () => {
    try {
      await api.patch(`/tenants/${sel.id}`, {
        brand_color: sel.brand_color,
        propay_fee_pct: sel.propay_fee_pct,
        early_payment_fee_rate_per_30: sel.early_payment_fee_rate_per_30,
        customer_terms_days: sel.customer_terms_days,
        primary_contact_email: sel.primary_contact_email,
        primary_contact_name: sel.primary_contact_name,
      });
      toast.success("Saved");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Max 2 MB");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/tenants/${sel.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSel({ ...sel, logo_url: data.logo_url });
      load();
      toast.success("Logo uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const logoSrc = sel.logo_url?.startsWith("http") ? sel.logo_url : `${BACKEND}${sel.logo_url}`;

  return (
    <div className="space-y-6">
      <Section title="Customers & branding" subtitle="Configure per-FMCG commercials and upload company logos so influencers see who is engaging them."
        right={<Button data-testid={TID.saveConfigBtn} onClick={save} className="bg-[#0052FF] hover:bg-[#0043D9]">Save configuration</Button>} />

      {tenants.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {tenants.map((t) => (
            <button key={t.id} onClick={() => setSel(t)} className={`px-3 py-1.5 rounded border text-sm ${sel.id === t.id ? "bg-[#0052FF] text-white border-[#0052FF]" : "border-slate-200"}`}>{t.name}</button>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h3 className="font-heading font-bold text-slate-900">Branding</h3>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-2 block">Company logo</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 grid place-items-center overflow-hidden shrink-0">
                {sel.logo_url ? <img src={logoSrc} alt="logo" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-slate-400 text-center px-1">No logo</span>}
              </div>
              <div className="flex-1">
                <input id="logo-input" data-testid={TID.tenantLogoInput} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden"
                  onChange={(e) => upload(e.target.files?.[0])} />
                <label htmlFor="logo-input" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 hover:border-slate-400 cursor-pointer text-sm font-semibold bg-white">
                  {uploading ? "Uploading…" : sel.logo_url ? "Replace logo" : "Upload logo"}
                </label>
                <div className="text-xs text-slate-500 mt-2">PNG, JPG, SVG or WEBP. Max 2 MB. Shown on POs and influencer notifications.</div>
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Brand colour</label>
            <div className="flex gap-3 items-center">
              <input type="color" value={sel.brand_color || "#0052FF"} onChange={(e) => setSel({ ...sel, brand_color: e.target.value })} className="w-11 h-11 rounded border cursor-pointer" />
              <Input value={sel.brand_color || ""} onChange={(e) => setSel({ ...sel, brand_color: e.target.value })} className="max-w-[160px] font-mono" />
            </div>
          </div>

          <div className="pt-4 border-t">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-3">Preview</div>
            <div className="rounded-lg border bg-slate-900 text-white p-4 flex items-center gap-3">
              {sel.logo_url && <img src={logoSrc} alt="logo" className="h-8 w-auto bg-white rounded px-1.5 py-1" />}
              <div>
                <div className="font-heading font-bold">{sel.name}</div>
                <div className="text-xs" style={{ color: sel.brand_color }}>via PROPAY GLOBAL PRIVATE LIMITED</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border p-6 space-y-4">
          <h3 className="font-heading font-bold text-slate-900">Commercials & terms</h3>
          <div>
            <label className="text-xs font-semibold">ProPay fee (% of order value)</label>
            <Input type="number" step="0.5" value={sel.propay_fee_pct ?? 0} onChange={(e) => setSel({ ...sel, propay_fee_pct: parseFloat(e.target.value) })} />
          </div>
          <div>
            <label className="text-xs font-semibold">Early Payment Fee rate (% per 30 days)</label>
            <Input type="number" step="0.1" value={sel.early_payment_fee_rate_per_30 ?? 0} onChange={(e) => setSel({ ...sel, early_payment_fee_rate_per_30: parseFloat(e.target.value) })} />
          </div>
          <div>
            <label className="text-xs font-semibold">FMCG payment terms to ProPay (days)</label>
            <Input type="number" value={sel.customer_terms_days ?? 60} onChange={(e) => setSel({ ...sel, customer_terms_days: parseInt(e.target.value) })} />
          </div>
          <div className="pt-3 border-t">
            <label className="text-xs font-semibold">Primary contact for deliverable approvals</label>
            <Input placeholder="Name" value={sel.primary_contact_name || ""} onChange={(e) => setSel({ ...sel, primary_contact_name: e.target.value })} className="mt-1" />
            <Input placeholder="Email" type="email" value={sel.primary_contact_email || ""} onChange={(e) => setSel({ ...sel, primary_contact_email: e.target.value })} className="mt-2" />
          </div>
          <div className="text-xs text-slate-500 pt-2 border-t">
            ProPay fee and Early Payment Fee are strictly confidential between FMCG and ProPay — never shown on the influencer PO or portal.
          </div>
        </div>
      </div>
    </div>
  );
}
